import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import FormField from '../components/ui/FormField';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import roleManager, { PERMISSIONS } from '../utils/roleManager.js';
import { apiClient } from '../utils/apiClient';
import { formatMoney, SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from '../utils/currency';
import { hostnameFromLine, isDomainProductType, tldFromProductName } from '../utils/siteHostname';
import './Quotes.css';
import { confirmDialog } from '../utils/dialogs';

const STATUSES = ['draft', 'sent', 'accepted', 'declined', 'expired'];

// Mirrors STATUS_TRANSITIONS in app/controllers/quotes.controller.js - the
// API is the one that enforces this, the copy here just keeps the dropdown
// from offering a move that's going to come back as a 400.
const STATUS_TRANSITIONS = {
  draft: ['sent', 'declined', 'expired'],
  sent: ['accepted', 'declined', 'expired', 'draft'],
  accepted: [],
  declined: ['draft', 'sent'],
  expired: ['draft', 'sent']
};

const EMPTY_FORM = {
  user_id: '',
  title: '',
  currency: DEFAULT_CURRENCY,
  tax_rate: '20',
  valid_until: '',
  status: 'draft',
  notes: '',
  line_items: []
};

const customerLabel = (customer) => {
  if (!customer) return '—';
  return customer.companyName || [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email;
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Same arithmetic as normalizeLineItems on the API: discount is an amount
// off the unit price, then quantity multiplies the net.
const netUnit = (line) => round2(
  Math.max(0, (Number(line.line_item_full_price) || 0) - (Number(line.item_discount) || 0))
);
const lineTotal = (line) => round2((Number(line.quantity) || 1) * netUnit(line));
const lineProfit = (line) => round2(
  lineTotal(line) - (Number(line.quantity) || 1) * (Number(line.line_item_cost) || 0)
);

function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [converting, setConverting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [sending, setSending] = useState(null);
  const [banner, setBanner] = useState('');
  const [bannerType, setBannerType] = useState('success');
  const showSuccess = (text) => { setBannerType('success'); setBanner(text); };
  const showError = (text) => { setBannerType('error'); setBanner(text); };
  const [catalog, setCatalog] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemList, setShowItemList] = useState(false);

  const canManage = roleManager.hasPermission(PERMISSIONS.MANAGE_QUOTES);
  const canConvert = roleManager.hasPermission(PERMISSIONS.CONVERT_QUOTES);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [quoteRows, users, items] = await Promise.all([
        apiClient.get('/api/quotes'),
        apiClient.post('/api/users/findall', {}),
        // The same catalog the Add Invoice page prices from, so a quote and
        // an invoice for the same work can't disagree on cost or price.
        apiClient.get('/api/PurchasableItems')
      ]);
      setQuotes(quoteRows);
      setCustomers(users);
      setCatalog(items);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (quote) => {
    setForm({
      user_id: quote.user_id,
      title: quote.title,
      currency: quote.currency,
      tax_rate: quote.tax_rate,
      valid_until: quote.valid_until || '',
      status: quote.status,
      notes: quote.notes || '',
      line_items: (quote.line_items || []).map((item) => ({
        product_id: item.product_id ?? null,
        item_name: item.item_name ?? item.description ?? '',
        item_description: item.item_description ?? '',
        product_type: item.product_type ?? 'Quoted Work',
        product_category_id: item.product_category_id ?? null,
        remarks: item.remarks ?? '',
        quantity: String(item.quantity ?? 1),
        line_item_cost: String(item.line_item_cost ?? item.unit_cost ?? 0),
        line_item_full_price: String(item.line_item_full_price ?? item.unit_price ?? 0),
        item_discount: String(item.item_discount ?? 0)
      }))
    });
    setEditing(quote);
    setFormError('');
    setShowForm(true);
  };

  const setField = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const setLine = (index, name) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.map((line, i) => (i === index ? { ...line, [name]: value } : line))
    }));
  };

  // Picking a catalog item copies its cost and price onto the line rather
  // than linking to it, so re-pricing the catalog later can't silently
  // change what a customer was already quoted.
  const addCatalogItem = (item) => {
    setForm((prev) => ({
      ...prev,
      line_items: [...prev.line_items, {
        product_id: item.id,
        item_name: item.item_name,
        item_description: item.item_description || '',
        product_type: item.item_type || 'Quoted Work',
        product_category_id: item.item_category_id ? Number(item.item_category_id) : null,
        remarks: '',
        quantity: '1',
        line_item_cost: String(item.item_cost ?? 0),
        line_item_full_price: String(item.item_price ?? 0),
        item_discount: '0'
      }]
    }));
    setItemSearch('');
    setShowItemList(false);
  };

  const removeLine = (index) => setForm((prev) => ({
    ...prev,
    line_items: prev.line_items.filter((_, i) => i !== index)
  }));

  // Live preview of what the API will store - same arithmetic as
  // app/utils/money.js calculateTotals, rounded at each step.
  const totals = useMemo(() => {
    const subtotal = round2(form.line_items.reduce((sum, line) => sum + lineTotal(line), 0));
    const tax = round2(subtotal * ((Number(form.tax_rate) || 0) / 100));
    return { subtotal, tax, total: round2(subtotal + tax) };
  }, [form.line_items, form.tax_rate]);

  const save = async (e) => {
    e.preventDefault();
    const usableLines = form.line_items.filter((line) => String(line.item_name || '').trim() !== '');
    if (!form.user_id || !form.title.trim()) {
      setFormError('Customer and title are both required.');
      return;
    }
    if (usableLines.length === 0) {
      setFormError('Add at least one item from the catalog.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        user_id: form.user_id,
        title: form.title,
        currency: form.currency,
        tax_rate: form.tax_rate,
        valid_until: form.valid_until || null,
        status: form.status,
        notes: form.notes,
        line_items: usableLines
      };
      if (editing) {
        await apiClient.put(`/api/quotes/${editing.id}`, payload);
      } else {
        await apiClient.post('/api/quotes', payload);
      }
      setShowForm(false);
      await load();
    } catch (error) {
      console.error('Error saving quote:', error);
      setFormError(error.message || 'Could not save this quote.');
    } finally {
      setSaving(false);
    }
  };

  const convert = async (quote) => {
    const confirmed = await confirmDialog(
      `Convert ${quote.quote_number} into a real invoice for ${formatMoney(quote.total, quote.currency)}?\n\n` +
      'This raises an invoice and its line items. A quote can only ever be converted once.'
    );
    if (!confirmed) return;

    setConverting(quote.id);
    setBanner('');
    try {
      const result = await apiClient.post(`/api/quotes/${quote.id}/convert`, {});
      showSuccess(result.message);
      await load();
    } catch (error) {
      console.error('Error converting quote:', error);
      showError(error.message || 'Could not convert this quote.');
    } finally {
      setConverting(null);
    }
  };

  const downloadPdf = async (quote) => {
    setDownloading(quote.id);
    try {
      await apiClient.download(`/api/quotes/${quote.id}/pdf`, `${quote.quote_number}.pdf`);
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      showError(error.message || 'Could not download this quote.');
    } finally {
      setDownloading(null);
    }
  };

  const sendToCustomer = async (quote) => {
    if (!await confirmDialog(`Email quote ${quote.quote_number} to ${customerLabel(quote.customer)}?`)) return;

    setSending(quote.id);
    setBanner('');
    try {
      await apiClient.post(`/api/quotes/${quote.id}/send`, {});
      showSuccess(`Quote ${quote.quote_number} emailed to the customer.`);
      await load();
    } catch (error) {
      console.error('Error sending quote:', error);
      showError(error.message || 'Could not send this quote.');
    } finally {
      setSending(null);
    }
  };

  const remove = async (quote) => {
    if (!await confirmDialog(`Delete quote ${quote.quote_number}? This can't be undone.`)) return;
    try {
      await apiClient.delete(`/api/quotes/${quote.id}`);
      await load();
    } catch (error) {
      showError(error.message || 'Could not delete this quote.');
    }
  };

  const visibleQuotes = statusFilter ? quotes.filter((q) => q.status === statusFilter) : quotes;

  const search = itemSearch.trim().toLowerCase();
  const matchingItems = !showItemList ? [] : catalog.filter((item) => {
    if (!search) return true;
    return String(item.item_name || '').toLowerCase().includes(search)
      || String(item.item_description || '').toLowerCase().includes(search)
      || String(item.item_type || '').toLowerCase().includes(search);
  });

  // An accepted quote's price is frozen server-side (see quotes.controller.js
  // update) - reflect that here rather than letting someone type into fields
  // whose save is going to come back as a 409.
  const pricingLocked = editing?.status === 'accepted';

  const statusOptions = editing
    ? [editing.status, ...(STATUS_TRANSITIONS[editing.status] || [])]
    : ['draft', 'sent'];

  const columns = [
    { key: 'quote_number', label: 'Quote #', sortable: true },
    { key: 'title', label: 'Title', sortable: true },
    { key: 'customer', label: 'Customer', render: (row) => customerLabel(row.customer) },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      sortValue: (row) => Number(row.total),
      render: (row) => formatMoney(row.total, row.currency)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`quote-status quote-status-${row.status}`}>{row.status}</span>
      )
    },
    {
      key: 'valid_until',
      label: 'Valid Until',
      sortable: true,
      render: (row) => (row.valid_until ? new Date(row.valid_until).toLocaleDateString() : '—')
    },
    {
      key: 'converted_invoice_number',
      label: 'Invoice',
      render: (row) => row.converted_invoice_number || '—'
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="quote-actions">
          <Button
            variant="secondary"
            disabled={downloading === row.id}
            onClick={(e) => { e.stopPropagation(); downloadPdf(row); }}
          >
            {downloading === row.id ? 'Downloading...' : 'Download PDF'}
          </Button>
          {canManage && (
            <Button
              variant="secondary"
              disabled={sending === row.id}
              onClick={(e) => { e.stopPropagation(); sendToCustomer(row); }}
            >
              {sending === row.id ? 'Sending...' : 'Send to Customer'}
            </Button>
          )}
          {canManage && !row.converted_invoice_number && (
            <Button variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          )}
          {canConvert && row.status === 'accepted' && !row.converted_invoice_number && (
            <Button
              onClick={(e) => { e.stopPropagation(); convert(row); }}
              disabled={converting === row.id}
            >
              {converting === row.id ? 'Converting...' : 'Convert to Invoice'}
            </Button>
          )}
          {canManage && !row.converted_invoice_number && (
            <Button variant="danger" onClick={(e) => { e.stopPropagation(); remove(row); }}>Delete</Button>
          )}
        </div>
      )
    }
  ];

  return (
    <PageShell>
      <div className="quotes-header">
        <div>
          <h1>Quotes</h1>
          <p className="quotes-intro">
            Estimates live separately from invoices - nothing a quote contains reaches the sales
            figures until it's accepted and converted.
          </p>
        </div>
        {canManage && <Button onClick={openNew}>New Quote</Button>}
      </div>

      {banner && (
        <div
          className={`quotes-banner quotes-banner-${bannerType}`}
          role={bannerType === 'error' ? 'alert' : 'status'}
        >
          {banner}
          <button type="button" className="quotes-banner-close" onClick={() => setBanner('')} aria-label="Dismiss">&times;</button>
        </div>
      )}

      <div className="quotes-filter">
        <label htmlFor="statusFilter">Status</label>
        <select id="statusFilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <p>Loading quotes...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={visibleQuotes}
          rowKey="id"
          emptyMessage="No quotes raised yet."
        />
      )}

      {showForm && (
        <Modal
          open
          onClose={() => setShowForm(false)}
          title={editing ? `Edit ${editing.quote_number}` : 'New Quote'}
          size="large"
        >
          <form className="quote-form" onSubmit={save}>
            <div className="quote-form-row">
              <FormField id="user_id" label="Customer" required>
                <select id="user_id" value={form.user_id} onChange={setField('user_id')} disabled={!!editing}>
                  <option value="">Select a customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{customerLabel(c)} — {c.email}</option>
                  ))}
                </select>
              </FormField>
              <FormField id="status" label="Status">
                <select id="status" value={form.status} onChange={setField('status')}>
                  {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FormField>
            </div>

            <FormField id="title" label="Title" required>
              <input id="title" value={form.title} onChange={setField('title')} placeholder="Website rebuild + 12 months hosting" />
            </FormField>

            <div className="quote-lines">
              {!pricingLocked && (
                <div className="quote-item-search">
                  <label htmlFor="itemSearch">Add an item from the catalog</label>
                  <input
                    id="itemSearch"
                    type="text"
                    value={itemSearch}
                    placeholder="Search by name, description or type (e.g. .co.uk, Hosting)"
                    onChange={(e) => { setItemSearch(e.target.value); setShowItemList(true); }}
                    onFocus={() => setShowItemList(true)}
                  />
                  {matchingItems.length > 0 && (
                    <ul className="quote-item-list">
                      {matchingItems.map((item) => (
                        <li key={item.id}>
                          <button type="button" onClick={() => addCatalogItem(item)}>
                            <span className="quote-item-type">{item.item_type}</span>
                            <span className="quote-item-name">{item.item_name}</span>
                            <span className="quote-item-desc">{item.item_description}</span>
                            <span className="quote-item-price">{formatMoney(item.item_price, form.currency)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {showItemList && (
                    <button type="button" className="quote-item-close" onClick={() => setShowItemList(false)}>
                      Close list
                    </button>
                  )}
                </div>
              )}

              {form.line_items.length === 0 ? (
                <p className="quote-lines-empty">No items yet — search the catalog above to add one.</p>
              ) : (
                <div className="quote-lines-table-wrap">
                  <table className="quote-lines-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Cost</th>
                        <th>Price</th>
                        <th>Discount %</th>
                        <th>Line total</th>
                        <th>Profit</th>
                        <th>Remarks (shown on the invoice)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.line_items.map((line, index) => {
                        const price = Number(line.line_item_full_price) || 0;
                        const discountPct = price > 0
                          ? round2(((Number(line.item_discount) || 0) / price) * 100)
                          : 0;
                        return (
                          <tr key={index}>
                            <td data-label="Type">{line.product_type}</td>
                            <td data-label="Item">
                              <div className="quote-line-name">{line.item_name}</div>
                              {line.item_description && <div className="quote-line-desc">{line.item_description}</div>}
                            </td>
                            <td data-label="Qty">
                              <input
                                type="number" min="1" step="1"
                                aria-label={`Quantity for ${line.item_name}`}
                                value={line.quantity}
                                disabled={pricingLocked}
                                onChange={setLine(index, 'quantity')}
                              />
                            </td>
                            <td data-label="Cost">{formatMoney(line.line_item_cost, form.currency)}</td>
                            <td data-label="Price">{formatMoney(line.line_item_full_price, form.currency)}</td>
                            <td data-label="Discount %">
                              <input
                                type="number" min="0" max="100" step="0.01"
                                aria-label={`Discount for ${line.item_name}`}
                                value={discountPct}
                                disabled={pricingLocked}
                                onChange={(e) => {
                                  // Typed as a percentage but stored as an
                                  // amount, so a later price change can't
                                  // silently move the discount with it.
                                  const pct = Math.min(Math.max(Number(e.target.value) || 0, 0), 100);
                                  setForm((prev) => ({
                                    ...prev,
                                    line_items: prev.line_items.map((l, i) => (
                                      i === index ? { ...l, item_discount: String(round2(price * (pct / 100))) } : l
                                    ))
                                  }));
                                }}
                              />
                            </td>
                            <td data-label="Line total">{formatMoney(lineTotal(line), form.currency)}</td>
                            <td data-label="Profit" className={lineProfit(line) < 0 ? 'quote-line-loss' : undefined}>
                              {formatMoney(lineProfit(line), form.currency)}
                            </td>
                            <td data-label="Remarks">
                              <div className="quote-remarks">
                                <div className="quote-remarks-input">
                                  <input
                                    type="text"
                                    aria-label={`Remarks for ${line.item_name}`}
                                    value={line.remarks}
                                    disabled={pricingLocked}
                                    placeholder={isDomainProductType(line.product_type) ? 'belveder' : 'e.g. belveder.co.uk'}
                                    onChange={setLine(index, 'remarks')}
                                  />
                                  {/* On a domain line the catalog item is the
                                      suffix, so show it against the box - you
                                      only type the part that varies. */}
                                  {tldFromProductName(line.item_name) && (
                                    <span className="quote-remarks-tld">{line.item_name}</span>
                                  )}
                                </div>
                                {(() => {
                                  const tracked = hostnameFromLine(line);
                                  if (tracked) {
                                    return <span className="quote-remarks-ok">Tracked as {tracked}</span>;
                                  }
                                  if (isDomainProductType(line.product_type)) {
                                    return <span className="quote-remarks-warn">Not trackable — put the site name here</span>;
                                  }
                                  return null;
                                })()}
                              </div>
                            </td>
                            <td data-label="">
                              <button
                                type="button"
                                className="quote-line-remove"
                                onClick={() => removeLine(index)}
                                disabled={pricingLocked}
                                aria-label={`Remove ${line.item_name}`}
                              >
                                &times;
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {pricingLocked && (
                <p className="quote-locked-note">
                  This quote has been accepted, so what it costs is fixed. Raise a new quote if the
                  price needs to change.
                </p>
              )}
            </div>

            <div className="quote-form-row">
              <FormField id="currency" label="Currency">
                <select id="currency" value={form.currency} onChange={setField('currency')} disabled={pricingLocked}>
                  {Object.keys(SUPPORTED_CURRENCIES).map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </FormField>
              <FormField id="tax_rate" label="VAT / Tax (%)">
                <input id="tax_rate" type="number" step="0.01" min="0" max="100" value={form.tax_rate} onChange={setField('tax_rate')} disabled={pricingLocked} />
              </FormField>
              <FormField id="valid_until" label="Valid Until">
                <input id="valid_until" type="date" value={form.valid_until} onChange={setField('valid_until')} />
              </FormField>
            </div>

            <div className="quote-totals">
              <div><span>Subtotal</span><strong>{formatMoney(totals.subtotal, form.currency)}</strong></div>
              <div><span>Tax</span><strong>{formatMoney(totals.tax, form.currency)}</strong></div>
              <div className="quote-totals-grand"><span>Total</span><strong>{formatMoney(totals.total, form.currency)}</strong></div>
            </div>

            <FormField id="notes" label="Notes">
              <textarea id="notes" rows="3" value={form.notes} onChange={setField('notes')} />
            </FormField>

            {formError && <div className="quote-form-error">{formError}</div>}

            <div className="quote-form-actions">
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </PageShell>
  );
}

export default Quotes;
